import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export type PhaseFlags = { for_repair: boolean; for_refurbish: boolean; estimated_minutes?: number | null };

const categoryToApplianceType: Record<string, string> = {
  "washing-machine": "Washing Machine",
  "washing machine": "Washing Machine",
  "dryer": "Dryer",
  "tumble dryer": "Dryer",
  "washer-dryer": "Washer Dryer",
  "washer dryer": "Washer Dryer",
  "dishwasher": "Dishwasher",
  "oven": "Oven",
  "refrigerator": "Household Refrigerator",
  "household refrigerator": "Household Refrigerator",
};

const normalizeApplianceType = (category: string): string => {
  const lower = (category || "").toLowerCase();
  return categoryToApplianceType[lower] || category;
};

const normalizeBrand = (brand: string): string => {
  const b = (brand || "").trim();
  if (!b) return b;
  const upper = b.toUpperCase();
  if (upper === "AEG") return "AEG";
  if (upper === "LG") return "LG";
  return b.charAt(0).toUpperCase() + b.slice(1).toLowerCase();
};

/**
 * Returns a map of phase_category -> {for_repair, for_refurbish}
 * with brand-specific overrides merged on top of parent (brand=null) defaults.
 *
 * If no row exists for a phase, it is treated as enabled for both
 * (preserves backward compatibility).
 */
export function usePhaseDefaults(applianceCategory: string | null, brand: string | null) {
  const [flagsByPhase, setFlagsByPhase] = useState<Record<string, PhaseFlags>>({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!applianceCategory) return;
    const applianceType = normalizeApplianceType(applianceCategory);
    const normalizedBrand = brand ? normalizeBrand(brand) : null;

    const fetchFlags = async () => {
      setIsLoading(true);
      try {
        const { data: parent } = await supabase
          .from("workflow_phase_defaults")
          .select("phase_category, for_repair, for_refurbish, estimated_minutes")
          .eq("appliance_type", applianceType)
          .is("brand", null);

        const merged: Record<string, PhaseFlags> = {};
        for (const row of parent || []) {
          merged[row.phase_category] = {
            for_repair: row.for_repair,
            for_refurbish: row.for_refurbish,
            estimated_minutes: (row as any).estimated_minutes ?? null,
          };
        }

        if (normalizedBrand) {
          const { data: brandRows } = await supabase
            .from("workflow_phase_defaults")
            .select("phase_category, for_repair, for_refurbish, estimated_minutes")
            .eq("appliance_type", applianceType)
            .eq("brand", normalizedBrand);
          for (const row of brandRows || []) {
            const existing = merged[row.phase_category];
            merged[row.phase_category] = {
              for_repair: row.for_repair,
              for_refurbish: row.for_refurbish,
              estimated_minutes:
                (row as any).estimated_minutes ?? existing?.estimated_minutes ?? null,
            };
          }
        }

        setFlagsByPhase(merged);
      } catch (err) {
        console.error("usePhaseDefaults error", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFlags();
  }, [applianceCategory, brand]);

  const isPhaseEnabled = useCallback(
    (phaseCategory: string, workflowType: "repair" | "refurbish"): boolean => {
      const flags = flagsByPhase[phaseCategory];
      if (!flags) return true;
      return workflowType === "repair" ? flags.for_repair : flags.for_refurbish;
    },
    [flagsByPhase]
  );

  const getPhaseMinutes = useCallback(
    (phaseCategory: string): number | null => {
      const v = flagsByPhase[phaseCategory]?.estimated_minutes;
      return v == null ? null : Number(v);
    },
    [flagsByPhase]
  );

  return { flagsByPhase, isPhaseEnabled, getPhaseMinutes, isLoading };
}
